<script setup lang="ts">
import { ref, onMounted, toRefs, provide, watch } from 'vue';
import useVuelidate from '@vuelidate/core';

import Dialog from 'primevue/dialog';
import SelectButton from 'primevue/selectbutton';

import OrganizationContractMainForm from './OrganizationContractMainForm.vue';
import OrganizationContractDisciplines from './OrganizationContractDisciplines.vue';
import OrganizationContractClinicians from './OrganizationContractClinicians.vue';

import { ContractDetailInterface, DisciplineInterface } from '@/types';
import { CONTRACT_ENUMS, EMIT } from '@/enum';
import { useOrganizationContractsStore } from '@/store';

interface PropsInterface {
  id?: number;
}

const emit = defineEmits<{
  (e: EMIT.CANCEL): void;
  (e: EMIT.SAVE): void;
}>();

const contractStore = useOrganizationContractsStore();
const scope = 'contract_validation_scope';
const displayModal = ref(true);
const contract = ref<ContractDetailInterface>({} as ContractDetailInterface);
const selectedForm = ref<CONTRACT_ENUMS.FORMS>(CONTRACT_ENUMS.FORMS.FLAT_FEE);
const props = defineProps<PropsInterface>();
const { id } = toRefs(props);
const canEdit = contractStore.canEditOrCreateContracts;

onMounted(async () => {
  contract.value = contractStore.contract;
  checkDisciplinesForBillingType();
});

const v$ = useVuelidate({ $scope: scope });

const billingTypes = [
  { id: CONTRACT_ENUMS.FORMS.FLAT_FEE, name: CONTRACT_ENUMS.CONTRACT_BILLING_TYPE.FLAT_FEE, label: 'Flat Fee' },
  { id: CONTRACT_ENUMS.FORMS.HOURLY, name: CONTRACT_ENUMS.CONTRACT_BILLING_TYPE.HOURLY, label: 'Hourly' },
  {
    id: CONTRACT_ENUMS.FORMS.PER_STUDENT,
    name: CONTRACT_ENUMS.CONTRACT_BILLING_TYPE.PER_STUDENT,
    label: 'Per Student',
  },
];

/**
 * Handle the closing of the create modal.
 */
const onCancel = () => {
  contractStore.pendingRequests = [];
  displayModal.value = false;
  emit(EMIT.CANCEL);
};

watch(selectedForm, (newForm) => {
  findBillingTypeNameById(newForm);
});

const findBillingTypeNameById = (id: number) => {
  contractStore.contract.billing_type = billingTypes.find((item) => item.id === id)?.name;
};

const checkDisciplinesForBillingType = () => {
  if (contract.value.disciplines.length) {
    const contractType = (contract.value.disciplines[0] as DisciplineInterface).contract_type;
    const billingType = billingTypes.find((item) => item.name === contractType);
    selectedForm.value = billingType?.id ?? 0;

    findBillingTypeNameById(selectedForm.value);
  } else {
    contractStore.contract.billing_type = billingTypes[0].name;
  }
};

/**
 * Handle the saving and closing of the modal.
 */
const onSave = async () => {
  contractStore.contract = contract.value;
  contractStore.contract.billing_type = billingTypes.find((item) => item.id === selectedForm.value)?.name;
  const validated = await v$.value.$validate();
  if (!validated) {
    const failingFormElement =
      document.querySelectorAll('.p-invalid')[0] || document.querySelector('.top-form-selector');

    failingFormElement.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });

    return;
  }
  displayModal.value = false;

  if (id?.value && contractStore.editType == CONTRACT_ENUMS.CONTRACT_EDIT_TYPES.EDIT) {
    await contractStore
      .save()
      .then(() => contractStore.doToast('Contract has been saved', 'Success'))
      .catch(() => {
        contractStore.doToast('Error saving contract, contact eLuma support', 'Error', true);
        emit(EMIT.CANCEL);
        return;
      });
  } else {
    await contractStore
      .createContract()
      .then(() => contractStore.doToast('Contract has been created', 'Success'))
      .catch(() => {
        contractStore.doToast('Error creating contract, contact eLuma support', 'Error', true);
        emit(EMIT.CANCEL);
        return;
      });
  }
  emit(EMIT.SAVE);
};

//provide the form type for subchildren
provide('selectedForm', selectedForm);
</script>

<template>
  <Dialog
    v-model:visible="displayModal"
    :modal="true"
    :header="`Contract for ${contractStore.organizationName}`"
    :draggable="false"
    position="center"
    :breakpoints="{ '2200px': '75vw', '1600px': '85vw', '1200px': '100vw', '960px': '100vw' }"
    class="contract-modal w-6"
    content-class="p-0 bg-white overflow-x-hidden"
    @update:visible="onCancel"
  >
    <div class="top-form-selector flex flex-wrap align-items-center p-4 pb-0">
      <div class="mr-4 mb-3">
        <SelectButton
          v-model="selectedForm"
          :unselectable="false"
          :options="billingTypes"
          :disabled="contractStore.editType == CONTRACT_ENUMS.CONTRACT_EDIT_TYPES.EDIT || !canEdit"
          option-label="label"
          option-value="id"
        />
      </div>
      <div class="flex flex-wrap mb-3">
        <div class="flex align-items-center mx-0 pr-3">
          <Skeleton v-if="contractStore.loading" height="24px" width="47px" />
          <InputSwitch
            v-else
            id="prepay"
            v-model="contract.prepay"
            class="switch"
            :true-value="1"
            :false-value="0"
            :disabled="!canEdit"
          />
          <label for="prepay" class="ml-2 font-semibold">Prepay</label>
        </div>
        <div class="flex align-items-center pr-3">
          <Skeleton v-if="contractStore.loading" height="24px" width="47px" />
          <InputSwitch
            v-else
            id="third_party"
            v-model="contract.third_party"
            class="switch"
            :true-value="1"
            :false-value="0"
            :disabled="!canEdit"
          />
          <label for="third_party" class="ml-2 font-semibold">3rd Party</label>
        </div>
        <div v-if="selectedForm === CONTRACT_ENUMS.FORMS.HOURLY" class="flex align-items-center">
          <Skeleton v-if="contractStore.loading" height="24px" width="47px" />
          <InputSwitch
            v-else
            id="third_party"
            v-model="contract.fee_for_service"
            class="switch"
            :true-value="1"
            :false-value="0"
            :disabled="!canEdit"
          />
          <label for="third_party" class="ml-2 font-semibold">Fee For Service</label>
        </div>
      </div>
    </div>
    <div v-if="contract" class="p-0">
      <OrganizationContractMainForm :scope="scope" :contract="contract" :loading="contractStore.loading" />
      <OrganizationContractDisciplines
        v-if="contract.disciplines.length"
        :scope="scope"
        :loading="contractStore.loading"
      />
      <OrganizationContractClinicians :clinicians="contract.clinicians" :loading="contractStore.loading" />
    </div>
    <template v-if="canEdit" #footer>
      <div class="flex flex-row justify-content-end">
        <AppButton id="cancel_btn" type="secondary" text="Cancel" @click="onCancel" />
        <AppButton id="save_btn" type="primary" text="Save" @click="onSave" />
      </div>
    </template>
  </Dialog>
</template>

<style lang="scss" scoped>
:deep(.clinician-rate) {
  height: 296px;
}

:deep(.p-chip) {
  background: var(--primary-color-dark);
  color: white;
  margin-right: 1rem;
}

.field {
  label {
    margin-right: 3px;
    font-size: 12px;
    line-height: 12px;
    color: $grayDark;
  }
  .data {
    font-size: 14px;
    line-height: 18px;
  }
}

:deep(.clinician-selector) {
  background-color: black;
  padding: 0.75rem;

  label {
    font-size: 20px;
  }

  button,
  button:hover,
  button:focus,
  button:enabled {
    width: 30px;
    border: none;
    background: transparent;
    box-shadow: none;
  }
}

:deep(.p-selectbutton) {
  border-radius: 4px !important;
  .p-button {
    width: 120px;
    height: 32px;
    justify-content: center;
    &:not(.p-highlight),
    &:not(.p-highlight):hover {
      border: 2px solid $white !important;
      background: $white !important;
      border-radius: 0 !important;
    }
    &:first-child {
      border-radius: 4px 0 0 4px !important;
    }
    &:last-child {
      border-radius: 0 4px 4px 0 !important;
    }
    .p-button-label {
      font-size: 14px;
      color: $grayDark !important;
      font-weight: 600;
      white-space: nowrap;
    }
    &:not(.p-highlight):hover {
      .p-button-label {
        color: var(--primary-color) !important;
      }
    }
    &.p-highlight,
    &.p-highlight:hover {
      background: var(--primary-color-light);
      border: 2px solid var(--primary-color-light);
      .p-button-label {
        color: var(--primary-color-dark) !important;
      }
    }
  }
}

.clinicians {
  p {
    font-size: 20px;
  }
}
</style>
